/**
 * SURVEY.TEMPLATE
 * Edit params for a template
 */

import {
  ALL_DESC_SCALES,
  allDescKey,
  customDescKey,
  getCustomDesc,
  getCustomDescBases
} from "@/utils/custom-desc.utils"
import { useLocation } from "react-router-dom"
import { toast } from "react-toastify"
import { useState } from "react"
import { connect } from "react-redux"
import { withTranslation, WithTranslation } from "react-i18next"
import { Template, TemplateState, TEMPLATES_TAGS } from "@/redux/template.types"
import DuplicateTemplateAskModal from "@/modals/duplicate-template-ask.modal"
import Checkbox from "@/components/checkbox"
import Card from "@/components/card"
import { store } from "@/index"
import {
  templateEdit,
  templateEditAndUpdate,
  templateEditDescription,
  templateEditLanguageAndUpdate,
  templateEditName,
  templateStatus,
  templateUpdate,
  templateUpdate_AsSupervisor
} from "@/redux/template.actions"
import { STATUS_SAVED, STATUS_SAVE_ERROR, STATUS_SAVING } from "@/redux/_status.types"
import TextInput from "@/components/text-input"
import ImagePicker from "@/components/image-picker"
import { Session } from "@/redux/_session.types"
import NavFocus from "@/nav/nav.focus"
import PageHeader from "@/components/page-header"
import Space from "@/components/space"
import Tabs from "@/components/tabs"
import EditDisplayWidget from "@/widgets/edit-display.widget"
import LanguageSelection from "@/components/language-selection"
import Chip from "@/components/chip"
import { Language } from "@/redux/language.types"
import { Topic, TopicState } from "@/redux/topic.types"
import { Axis, AxisState } from "@/redux/axis.types"
import { Question } from "@/redux/question.types"
import { Proposition } from "@/redux/proposition.types"
import { axisFetchAndGet, axisUpdate } from "@/redux/axis.actions"
import { topicFetchAndGet, topicUpdate } from "@/redux/topic.actions"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleNotch, faLanguage } from "@fortawesome/free-solid-svg-icons"
import TranslatorWidget from "@/widgets/translator.widget"
import Modal from "@/components/modal"
import TemplateOptionsWidget from "@/widgets/template-options.widget"
import EditorOptionsWidget from "@/widgets/editor-options.widget"

interface StateProps extends WithTranslation {
  _session: Session
  axis: AxisState
  template: TemplateState
  topic: TopicState
}

const TOPICS_CHUNKS_SIZE: number = 10

const CONTEXT_AUTO_TRANSLATION: string = "CONTEXT_AUTO_TRANSLATION"
const MODAL_DUPLICATE_ASK: string = "MODAL_DUPLICATE_ASK" //Modal for duplicate

function SurveyTemplate(props: StateProps) {
  const { t } = props

  //The shortcut of the editor opens the automatic translation directly
  const location = useLocation()
  const [currentModal, setCurrentModal] = useState<string | null>(
    (location.state as any)?.openTranslation ? CONTEXT_AUTO_TRANSLATION : null
  )

  const [currentTab, setCurrentTab] = useState(props.template.active.language)
  const [translateStatus, setTranslateStatus] = useState<string | null>(null)

  //Edit template and save just after
  function editTemplate(key, value) {
    store.dispatch(templateEditAndUpdate(key, value))
  }

  //Get tabs
  function getTabs() {
    const tab = {}
    props.template.active.languages.forEach((lg) => {
      tab[lg] = lg.toUpperCase()
    })
    return tab
  }

  function addLanguage(language: string) {
    store.dispatch(templateEditLanguageAndUpdate(language))
    setCurrentTab(language)
  }

  function editOptions(key, value) {
    store.dispatch(templateEditAndUpdate("options", Object.assign({}, props.template.active.options, { [key]: value })))
  }

  function selectLanguage(item: string) {
    store.dispatch(templateEdit("language", item))
    setCurrentTab(item)
  }

  //Keys of the descriptions of the answers of a question (only if the template lets the user write them for each question)
  function getCustomDescBasesOf(question: Question): string[] {
    return props.template.active.options.editResponseLabelByQuestion && question.options?.isCustomResponseLabel
      ? getCustomDescBases(question.responseCount)
      : []
  }

  //Keys of the texts written for the whole questionnaire (allDesc) : only the scales with custom texts
  function getAllDescKeys(): { count: number; key: string }[] {
    return ALL_DESC_SCALES.filter((scale) => props.template.active.options["response" + scale.count + "Label"]).flatMap(
      (scale) => scale.keys.flatMap((key) => [key, key + "_description"].map((k) => ({ count: scale.count, key: k })))
    )
  }

  //Language with the most texts : the one to translate from by default (not the first one of the list)
  function getDefaultSourceLanguage(): string {
    const languages = props.template.active.languages
    const texts: any[] = getTextsToTranslate()
    const counts = languages.map((lg) => texts.filter((x) => typeof x?.[lg] === "string" && x[lg].trim()).length)
    const best = languages.filter((_, i) => counts[i] === Math.max(...counts))
    //Tie : the language of the user, then the language being edited, then the first one
    return best.find((lg) => lg === props._session.language) || best.find((lg) => lg === currentTab) || best[0]
  }

  //Language to translate into by default : the language being edited, otherwise the first one that is not the source
  function getDefaultTargetLanguage(): string {
    const source = getDefaultSourceLanguage()
    return currentTab !== source ? currentTab : props.template.active.languages.find((lg) => lg !== source) || currentTab
  }

  function getTextsToTranslate(): string[] {
    const texts: string[] = []

    //Topics (includes questions and propositions)
    props.topic.list.forEach((topic: Topic) => {
      texts.push(topic.name)
      topic.Questions.forEach((question: Question) => {
        texts.push(question.name)
        question.Propositions!.forEach((proposition: Proposition) => {
          texts.push(proposition.name)
        })
        //Descriptions of the answers written for this question : one text per language
        getCustomDescBasesOf(question).forEach((base) => {
          const languages = props.template.active.languages
          texts.push(
            Object.fromEntries(languages.map((lg) => [lg, getCustomDesc(question.options, base, lg, languages)])) as any
          )
        })
      })
    })

    //Axes
    props.axis.list.forEach((axis: Axis) => {
      texts.push(axis.name)
    })

    //Texts of the answers for the whole questionnaire (allDesc), only for the scales with custom texts
    getAllDescKeys().forEach(({ count, key }) => {
      const options = props.template.active.options
      texts.push(Object.fromEntries(props.template.active.languages.map((lg) => [lg, options[allDescKey(count, key, lg)] || ""])) as any)
    })

    //Template
    texts.push(props.template.active.name)
    texts.push(props.template.active.description)

    return texts
  }

  async function translateTemplateTexts(texts: (string | null | undefined)[], language: Language) {
    let index: number = 0

    //A text that could not be translated (null) is left as it was : it stays in the alerts of the questionnaire
    const setTranslation = (names: any, code: string, text: string | null | undefined) => {
      if (text) names[code] = text
    }

    setTranslateStatus(t("starting_translation"))

    //Topics (includes questions and propositions)
    const topicsTranslated: Topic[] = props.topic.list
    topicsTranslated.forEach((topic: Topic) => {
      //edit topic
      setTranslation(topic.name, language.code, texts[index])
      index++
      topic.Questions.forEach((question: Question) => {
        //edit question
        setTranslation(question.name, language.code, texts[index])
        question.updated = true
        index++
        question.Propositions!.forEach((proposition: Proposition) => {
          //edit proposition
          setTranslation(proposition.name, language.code, texts[index])
          proposition.updated = true
          index++
        })
        //edit descriptions of the answers
        getCustomDescBasesOf(question).forEach((base) => {
          const text = texts[index]
          if (text) {
            question.options = { ...question.options, [customDescKey(base, language.code)]: text }
          }
          index++
        })
      })
    })

    for (let j: number = 0; j < topicsTranslated.length; j += TOPICS_CHUNKS_SIZE) {
      setTranslateStatus(t("translating_topics") + " " + j + "/" + topicsTranslated.length)
      await store.dispatch(topicUpdate(props.template.active, topicsTranslated.slice(j, j + TOPICS_CHUNKS_SIZE), []))
    }

    //Axes
    const axesTranslated: Axis[] = props.axis.list
    axesTranslated.map(async (axis: Axis) => {
      setTranslation(axis.name, language.code, texts[index])
      index++
    })

    for (let k: number = 0; k < axesTranslated.length; k++) {
      setTranslateStatus(t("translating_axes") + " " + k + "/" + axesTranslated.length)
      await store.dispatch(axisUpdate(axesTranslated[k], props.template.active.id))
    }

    //Texts of the answers for the whole questionnaire (allDesc)
    const templateTranslated: Template = props.template.active
    getAllDescKeys().forEach(({ count, key }) => {
      const text = texts[index]
      if (text) {
        templateTranslated.options = { ...templateTranslated.options, [allDescKey(count, key, language.code)]: text }
      }
      index++
    })

    //The options are displayed without reloading the page
    store.dispatch(templateEdit("options", templateTranslated.options))

    //Template
    setTranslation(templateTranslated.name, language.code, texts[index])
    index++
    setTranslation(templateTranslated.description, language.code, texts[index])
    setTranslateStatus(t("translating_template"))
    await store.dispatch(templateUpdate(templateTranslated))

    //Reload what has been saved (topics, questions, axes) : the editor displays it without reloading the page
    await store.dispatch(topicFetchAndGet(null))
    store.dispatch(axisFetchAndGet(templateTranslated.axesOrder, null))
    setTranslateStatus(null)

    const failed = texts.filter((x) => x === null).length
    if (failed > 0) {
      toast(t("auto_translate_failed", { count: failed }), { type: "error", autoClose: false })
    }
  }

  function removeLanguage(language: string) {
    //The tab of the language for the edition is one of the languages that remain (the props still hold the list from before the removal)
    const remaining: string[] = props.template.active.languages.filter((x: string) => x !== language)
    store.dispatch(templateEditLanguageAndUpdate(language))
    setCurrentTab(remaining[0])
  }

  //Save data
  //Function different if supervisor or not
  async function save() {
    if (props.template.active.isLocked(props._session.interfaceType)) {
      setCurrentModal(MODAL_DUPLICATE_ASK)
    } else {
      const updateFunction =
        props._session.interfaceType === "SUPERVISOR" ? templateUpdate_AsSupervisor : templateUpdate
      store.dispatch(templateStatus(STATUS_SAVING))
      const response: any = await store.dispatch(updateFunction(props.template.active))
      store.dispatch(templateStatus(response.error ? STATUS_SAVE_ERROR : STATUS_SAVED))
    }
  }

  return (
    <NavFocus>
      {currentModal === MODAL_DUPLICATE_ASK && <DuplicateTemplateAskModal onClose={() => setCurrentModal(null)} />}

      <PageHeader title={t("template_params")} />

      {props._session.interfaceType === "SUPERVISOR" ? (
        <Card title={t("supervisor_params")}>
          {
            /** si plusieurs langues alors onglets */
            props.template.active.languages.length > 1 && (
              <Tabs active={currentTab} tabs={getTabs()} onClick={(tab: string) => selectLanguage(tab)} />
            )
          }

          <TextInput
            onBlur={save}
            onChange={(e) => store.dispatch(templateEditName(currentTab, e.value))}
            status={props.template.status}
            title={t("template_name")}
            value={props.template.active.name[currentTab]}
          />

          <TextInput
            onBlur={save}
            isFullWidth
            onChange={(e) => store.dispatch(templateEditDescription(currentTab, e.value))}
            status={props.template.status}
            title={t("utils_description")}
            value={props.template.active.description[currentTab]}
          />

          <Checkbox
            active={props.template.active.draft}
            status={props.template.status}
            text={t("template_supervisor_draft")}
            onClick={() => editTemplate("draft", !props.template.active.draft)}
          />

          <Checkbox
            active={props.template.active.free}
            status={props.template.status}
            text={t("template_supervisor_free")}
            onClick={() => editTemplate("free", !props.template.active.free)}
          />

          <p className="grey-t">{t("template_tag")}</p>

          <div className="flex">
            {TEMPLATES_TAGS.map((tag, i) => (
              <Chip
                key={i}
                onClick={() => editOptions("tag", tag.id)}
                color={props.template.active.options.tag === tag.id ? props._session.accountColors.active : ""}
                isWithMargin>
                {t("template_tag_" + tag.id)}
              </Chip>
            ))}
          </div>
        </Card>
      ) : (
        <Card title={t("utils_informations")}>
          {
            /** si plusieurs langues alors onglets */
            props.template.active.languages.length > 1 && (
              <Tabs active={currentTab} tabs={getTabs()} onClick={(tab: string) => selectLanguage(tab)} />
            )
          }

          <TextInput
            onBlur={save}
            onChange={(e: any) => store.dispatch(templateEditName(currentTab, e.value))}
            status={props.template.status}
            title={t("template_name")}
            value={props.template.active.name[currentTab]}
          />
        </Card>
      )}

      <TemplateOptionsWidget />

      <EditorOptionsWidget />

      <Card title={t("utils_edit_display")}>
        <p className="grey-t">{t("utils_edit_display_help")}</p>
        <EditDisplayWidget
          checkboxes={[
            { id: "editorDisplayTopic", label: t("topic") },
            { id: "editorDisplayPrimary", label: t("question_priority") },
            { id: "editorDisplayQuestion", label: t("question") },
            { id: "editorDisplayTrigger", label: t("question_trigger") },
            { id: "editorDisplayType", label: t("question_type") },
            { id: "editorDisplayWithScore", label: t("question_score") },
            { id: "editorDisplayReverse", label: t("question_reverse") },
            { id: "editorDisplaySpecial", label: t("question_rps") },
            { id: "editorDisplayFullScreen", label: t("question_fullscreen") },
            { id: "editorDisplayAxis", label: t("axis") }
          ]}
        />
      </Card>

      <Card className="flex1" title={t("utils_lg_params")}>
        <div className="flex">
          <div>
            <p className="grey-t">{t("template_lg_available")}</p>

            <LanguageSelection
              onAdd={(language: Language) => addLanguage(language.code)}
              onDelete={(language: Language) => removeLanguage(language.code)}
              languages={props.template.active.languages}
              activeLang={currentTab}
              onClick={(language: Language) => selectLanguage(language.code)}
            />

            <div className="height-20" />
            <i>{t("template_lg_current")}</i>
          </div>

          <Space />

          <div>
            {translateStatus ? (
              <div className="flex">
                <FontAwesomeIcon icon={faCircleNotch} spin />
                {"  " + translateStatus}
              </div>
            ) : props.template.active.languages.length > 1 && props._session.isTranslationAllowed ? (
              <span className="_hover grey-t" style={{ cursor: "pointer" }} onClick={() => setCurrentModal(CONTEXT_AUTO_TRANSLATION)}>
                <FontAwesomeIcon icon={faLanguage} style={{ marginRight: 8 }} />
                <u>{t("auto_translate")}</u>
              </span>
            ) : (
              <div />
            )}
            {currentModal === CONTEXT_AUTO_TRANSLATION && (
              <Modal title={t("auto_translate")} onClose={() => setCurrentModal(null)}>
                <TranslatorWidget
                  allowAll
                  onClose={() => setCurrentModal(null)}
                  sourceLang={getDefaultSourceLanguage()}
                  targetLang={getDefaultTargetLanguage()}
                  onTranslate={async (texts: string[], language: Language, isLast: boolean) => {
                    await translateTemplateTexts(texts, language)
                    if (isLast) setCurrentModal(null)
                  }}
                  languages={props.template.active.languages}
                  sourceTexts={getTextsToTranslate()}
                  onTargetChange={(lang: Language) => setCurrentTab(lang.code)}
                />
              </Modal>
            )}
          </div>
        </div>
      </Card>

      <Card title={t("Image")}>
        <div className="flex">
          <ImagePicker
            id={props.template.active.id}
            square
            imageId={props.template.active.image ? props.template.active.image : null}
            model="Templates"
            onDelete={() => editTemplate("image", null)}
            onSave={() => editTemplate("image", props.template.active.id)}
          />
          <Space />
        </div>
      </Card>
    </NavFocus>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  axis: state.axis,
  template: state.template,
  topic: state.topic
})

export default connect(mapStateToProps)(withTranslation()(SurveyTemplate))
